import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "@/components/Toaster";
import { ThumbsUp, ThumbsDown, Filter, RefreshCw, ArrowLeft } from "lucide-react";

/**
 * AdaFeedback — Owner + Admin dashboard for Ada thumbs-up/down votes
 * with optional comments. A rolling window across every accessible
 * room, filterable by verdict / room / days. This is the closest
 * thing to a "how is Ada actually doing?" panel we can ship without
 * model fine-tuning.
 *
 * Route: /app/ada-feedback  (Admin or Owner only)
 */
export default function AdaFeedback() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const isOwner = user?.owner === true;

  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({ count: 0, up: 0, down: 0 });
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);
  const [verdict, setVerdict] = useState("");   // "" | "up" | "down"
  const [roomFilter, setRoomFilter] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("days", String(days));
      params.set("limit", "500");
      if (verdict) params.set("verdict", verdict);
      if (roomFilter) params.set("room_id", roomFilter);
      const { data } = await api.get(`/ada/feedback?${params}`);
      setItems(data.items || []);
      setSummary({ count: data.count || 0, up: data.up || 0, down: data.down || 0 });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load feedback");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [days, verdict, roomFilter]);

  const rooms = useMemo(() => {
    const map = new Map();
    for (const it of items) if (it.room_id) map.set(it.room_id, it.room_name || it.room_id);
    return Array.from(map.entries());
  }, [items]);

  if (!isAdmin && !isOwner) {
    return (
      <div className="max-w-md mx-auto py-16 text-center">
        <div className="text-sm text-muted-foreground">
          This page is for Owners and Admins.
        </div>
        <Link to="/app/rooms" className="mt-4 inline-block text-primary text-sm hover:underline">
          Back to Rooms
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto py-8 px-4" data-testid="ada-feedback-page">
      <div className="flex items-center gap-3 mb-6">
        <Link to="/app/rooms" className="p-1.5 rounded-md hover:bg-secondary text-muted-foreground hover:text-foreground transition" aria-label="Back to rooms">
          <ArrowLeft className="w-4 h-4" />
        </Link>
        <div>
          <h1 className="text-2xl font-semibold">Ada feedback</h1>
          <p className="text-sm text-muted-foreground">
            Every thumbs-up / thumbs-down + comment across your rooms. Use this to spot patterns and tune Ada from evidence, not guesses.
          </p>
        </div>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-3 gap-3 mb-6">
        <SummaryTile label="Total ratings" value={summary.count} tint="primary" />
        <SummaryTile label="Helpful" value={summary.up} tint="emerald" icon={<ThumbsUp className="w-4 h-4" />} />
        <SummaryTile label="Not helpful" value={summary.down} tint="red" icon={<ThumbsDown className="w-4 h-4" />} />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="flex items-center gap-1 text-xs text-muted-foreground">
          <Filter className="w-3.5 h-3.5" /> Filter:
        </div>
        <select
          value={days}
          onChange={(e) => setDays(parseInt(e.target.value, 10) || 30)}
          data-testid="feedback-days-filter"
          className="text-xs px-2 py-1 rounded-md bg-background border border-border"
        >
          <option value={7}>Last 7 days</option>
          <option value={14}>Last 14 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
          <option value={365}>Last year</option>
        </select>
        <select
          value={verdict}
          onChange={(e) => setVerdict(e.target.value)}
          data-testid="feedback-verdict-filter"
          className="text-xs px-2 py-1 rounded-md bg-background border border-border"
        >
          <option value="">All ratings</option>
          <option value="up">Helpful only</option>
          <option value="down">Not helpful only</option>
        </select>
        {rooms.length > 1 && (
          <select
            value={roomFilter}
            onChange={(e) => setRoomFilter(e.target.value)}
            data-testid="feedback-room-filter"
            className="text-xs px-2 py-1 rounded-md bg-background border border-border"
          >
            <option value="">All rooms</option>
            {rooms.map(([id, name]) => (
              <option key={id} value={id}>{name}</option>
            ))}
          </select>
        )}
        <button
          type="button"
          onClick={load}
          disabled={loading}
          data-testid="feedback-refresh"
          className="ml-auto text-xs inline-flex items-center gap-1 px-2 py-1 rounded-md border border-border hover:bg-secondary text-muted-foreground"
        >
          <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>

      {/* List */}
      {loading ? (
        <div className="py-16 text-center text-sm text-muted-foreground">Loading…</div>
      ) : items.length === 0 ? (
        <div className="py-16 text-center text-sm text-muted-foreground">
          No ratings in this window yet.
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((it) => (
            <FeedbackRow key={it.id} item={it} />
          ))}
        </div>
      )}
    </div>
  );
}

function SummaryTile({ label, value, tint, icon }) {
  const tintClasses = {
    primary: "bg-primary/8 border-primary/30 text-primary",
    emerald: "bg-emerald-500/8 border-emerald-500/30 text-emerald-600",
    red: "bg-red-500/8 border-red-500/30 text-red-600",
  }[tint] || "bg-secondary border-border text-foreground";
  return (
    <div className={`rounded-lg border px-4 py-3 ${tintClasses}`}>
      <div className="flex items-center gap-2 text-xs opacity-80">
        {icon}
        {label}
      </div>
      <div className="mt-1 text-2xl font-semibold tabular-nums">{value}</div>
    </div>
  );
}

function FeedbackRow({ item }) {
  const [expanded, setExpanded] = useState(false);
  const isDown = item.verdict === "down";
  const created = item.created_at ? new Date(item.created_at).toLocaleString() : "";
  return (
    <div className="rounded-lg border border-border bg-card px-4 py-3">
      <div className="flex items-center gap-2 mb-1.5">
        {isDown ? (
          <ThumbsDown className="w-3.5 h-3.5 text-red-600 shrink-0" />
        ) : (
          <ThumbsUp className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
        )}
        <span className="text-xs font-medium">{item.user_name || item.user_email || "Someone"}</span>
        <span className="text-xs text-muted-foreground">·</span>
        <Link to={`/app/rooms/${item.room_id}`} className="text-xs text-muted-foreground hover:text-primary hover:underline truncate">
          {item.room_name || "Room"}
        </Link>
        <span className="text-xs text-muted-foreground ml-auto">{created}</span>
      </div>
      {item.question && (
        <div className="text-xs text-muted-foreground mb-1">
          <span className="text-muted-foreground/70 mr-1">Q:</span>
          {item.question}
        </div>
      )}
      {item.comment && (
        <div className={`text-sm mt-1 mb-1 ${isDown ? "text-red-700 dark:text-red-400" : "text-emerald-700 dark:text-emerald-400"}`}>
          "{item.comment}"
        </div>
      )}
      {item.answer && (
        <div className="mt-1">
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="text-[11px] text-muted-foreground hover:text-foreground"
          >
            {expanded ? "Hide" : "Show"} answer
          </button>
          {expanded && (
            <div className="mt-1 text-xs text-muted-foreground bg-secondary/50 rounded-md p-2 whitespace-pre-wrap max-h-64 overflow-y-auto">
              {item.answer}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
